import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import ReviewDialog from "@/components/ReviewDialog";
import SelfiePreviewDialog from "@/components/SelfiePreviewDialog";
import ViewerBanner from "@/components/ViewerBanner";
import { useIsViewer } from "@/lib/permissions";
import { toast } from "sonner";
import { ChatCircle, CurrencyGbp, ShieldCheck } from "@phosphor-icons/react";
import { devWarn } from "@/lib/logger";

export default function CompanyBookings() {
  const viewer = useIsViewer();
  const [items, setItems] = useState([]);
  const load = () => api.get("/company/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const mark = async (id, attendance_status) => {
    try { await api.patch(`/company/bookings/${id}/attendance`, { attendance_status }); toast.success("Attendance recorded"); load(); }
    catch { toast.error("Failed"); }
  };
  const pay = async (b) => {
    try {
      const r = await api.post("/payments/checkout", { booking_id: b.id, origin_url: window.location.origin + "/company/payment/return" });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); }
  };
  const payoutStaff = async (b) => {
    if (!window.confirm(`Send £${b.net_amount} to the staff member's bank now?`)) return;
    try { await api.post(`/connect/payout/${b.id}`); toast.success("Payout sent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Payout failed"); }
  };
  const verifyPhoto = async (b) => {
    try { await api.patch(`/company/bookings/${b.id}/photo-verify?verified=true`); toast.success("Identity verified"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const startMsg = async (peerId, bookingId) => {
    try { await api.post("/messages", { receiver_id: peerId, booking_id: bookingId, message: "Hi 👋" }); }
    catch (err) { devWarn("seed greeting failed", err); /* non-critical, still open messages */ }
    window.location.href = "/company/messages";
  };
  return (
    <div className="space-y-4">
      {viewer && <ViewerBanner />}
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Bookings & attendance</h1>
      {items.length === 0 && <div className="text-slate-500 py-10 text-center">No bookings yet.</div>}
      <div className="space-y-3">
        {items.map((b) => (
          <div key={b.id} data-testid={`co-booking-${b.id}`} className="card-tactile p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display text-lg font-bold text-slate-900">{b.job?.title}</div>
                <div className="text-sm text-slate-600">{b.staff?.first_name} {b.staff?.last_name} · {b.staff?.email}</div>
                <div className="mt-1 text-sm text-slate-700">{b.job_date} · {b.start_time}–{b.end_time} · {b.job?.location}</div>
                {(b.face_match_verdict || b.photo_verified) && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <SelfiePreviewDialog booking={b} />
                    {b.photo_verified && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold" data-testid={`photo-verified-${b.id}`}>
                        <ShieldCheck size={12} weight="fill" /> Verified manually
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="text-right">
                <StatusBadge status={b.status} />
                <div className="mt-1 font-display text-lg font-extrabold text-slate-900">£{b.gross_amount}</div>
                {b.payment_status === "paid" && <div className="text-xs font-semibold text-emerald-600 mt-1">PAID</div>}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
              {!viewer && b.status === "confirmed" && (
                <>
                  <Button size="sm" onClick={()=>mark(b.id, "present")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`att-present-${b.id}`}>Mark present</Button>
                  <Button size="sm" variant="outline" onClick={()=>mark(b.id, "late")} data-testid={`att-late-${b.id}`}>Mark late</Button>
                  <Button size="sm" variant="outline" onClick={()=>mark(b.id, "no_show")} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`att-noshow-${b.id}`}>No-show</Button>
                </>
              )}
              {!viewer && b.face_match_verdict && !b.photo_verified && (
                <Button size="sm" variant="outline" onClick={()=>verifyPhoto(b)} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`verify-photo-${b.id}`}>
                  <ShieldCheck size={14} className="mr-1" /> Verify identity
                </Button>
              )}
              {!viewer && b.status === "completed" && b.payment_status !== "paid" && (
                <Button size="sm" onClick={()=>pay(b)} className="bg-slate-900 hover:bg-slate-800" data-testid={`pay-${b.id}`}>
                  <CurrencyGbp size={14} className="mr-1" /> Pay £{b.gross_amount}
                </Button>
              )}
              {!viewer && b.status === "completed" && b.payment_status === "paid" && b.payout_status !== "paid" && (
                <Button size="sm" onClick={()=>payoutStaff(b)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`payout-${b.id}`}>
                  Payout staff £{b.net_amount}
                </Button>
              )}
              {b.payout_status === "paid" && <span className="text-xs font-semibold text-emerald-600">Paid out to staff</span>}
              {!viewer && b.status === "completed" && <ReviewDialog booking={b} onDone={load} />}
              <Button size="sm" variant="outline" data-testid={`co-msg-${b.id}`} onClick={()=>startMsg(b.staff_id, b.id)}>
                <ChatCircle size={14} className="mr-1" /> Message staff
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
